import { createApi } from "@reduxjs/toolkit/query/react";
import type { IApiResponse } from "@/types";
import type { IAssignedEmployee } from "@/types/(company)/earnings.interface";

import type {
  ICreateBenefitRequest,
  TGetBenefitByIdRequest,
  TGetBenefitsListingRequest,
  TGetBenefitsListingResponse,
  TSingleBenefitResponse,
  TUpdateBenefitResponse,
} from "@/types/(company)/benefits.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyBenefitsApi = createApi({
  reducerPath: "companyBenefitsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["benefits"],
  endpoints: (builder) => ({
    createBenefit: builder.mutation<TSingleBenefitResponse, ICreateBenefitRequest>({
      query: (body) => ({
        url: "/company/settings/benefits",
        method: "POST",
        body,
      }),
      invalidatesTags: ["benefits"],
    }),

    getBenefits: builder.query<TGetBenefitsListingResponse, TGetBenefitsListingRequest>({
      query: (param) => ({
        url: "/company/settings/benefits",
        method: "GET",
        params: {
          // Sent only when set: an explicit `isActive=false` is a real filter,
          // so the absent case has to stay absent rather than default to false.
          ...(param?.query?.isActive === undefined
            ? {}
            : { isActive: param.query.isActive }),
          ...(param?.page ? { page: param.page } : {}),
          ...(param?.limit ? { limit: param.limit } : {}),
          // Search server-side: filtering a received page would only match the
          // rows that happen to be on it.
          ...(param?.search ? { search: param.search } : {}),
        },
      }),
      providesTags: ["benefits"],
    }),
    /**
     * The employees currently paid through a component — the avatar stack's
     * dialog. Separate from the list, which carries only a three-person
     * preview, because a core component can be on every employee in the company.
     */
    getBenefitAssignedEmployees: builder.query<
      IApiResponse<IAssignedEmployee[]>,
      { id: number; page?: number; limit?: number; branchId?: number }
    >({
      query: ({ id, page, limit, branchId }) => ({
        url: `/company/settings/benefits/${id}/assigned-employees`,
        method: "GET",
        // `branchId` omitted rather than sent as undefined: the API reads an
        // absent value as "every branch", which is what the filter's All means.
        params: { page, limit, ...(branchId ? { branchId } : {}) },
      }),
      providesTags: ["benefits"],
    }),

    getBenefitById: builder.query<TSingleBenefitResponse, TGetBenefitByIdRequest>({
      query: (param) => ({
        url: "/company/settings/benefits" + `/${param.id}`,
        method: "GET",
      }),
      providesTags: ["benefits"],
    }),

    updateBenefit: builder.mutation<TSingleBenefitResponse, TUpdateBenefitResponse>({
      query: ({ id, ...body }) => ({
        url: "/company/settings/benefits" + `/${id}`,
        method: "PATCH",
        body: body.data,
      }),
      invalidatesTags: ["benefits"],
    }),

    deleteBenefit: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: "/company/settings/benefits" + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["benefits"],
    }),
  }),
});

export const {
  useGetBenefitsQuery,
  useCreateBenefitMutation,
  useUpdateBenefitMutation,
  useDeleteBenefitMutation,
  useGetBenefitByIdQuery,
  useGetBenefitAssignedEmployeesQuery,
} = companyBenefitsApi;
